/**
 * Outcome types shared by every port contract in this directory
 * (WorkingCopyPort, RenewalSummaryPort, OnFilePort, RenewalDataPort,
 * DeclarationSubmissionPort). ExposurePrefillPort, the port these two
 * types originally served alongside, is retired (m1-exposure-detail-
 * sourcing TG-4) -- OnFilePort/RenewalDataPort supersede it.
 *
 * Two distinct shapes exist on purpose, per two separate ADRs:
 *
 * - {@link WriteOutcome} has THREE members: Succeeded, Failed and
 *   Indeterminate. ADR-20260821-kp45 is a hard constraint: a caller must be
 *   able to tell "definitely did not happen" (Failed) from "unknown, check
 *   before resubmitting" (Indeterminate). They must never collapse into one
 *   case, and at the HTTP surface neither maps to 502. ADR-20260826-94qx
 *   additionally requires that both outcomes retain the customer's answers
 *   on screen and offer the same confirm control, with distinct wording.
 *
 * - {@link ReadOutcome} has TWO members: Succeeded and Failed
 *   (ADR-20260918-gvy0). A read never mutates anything, so there is no
 *   "did it happen or not" ambiguity analogous to a write timeout —
 *   reusing the three-member write outcome for a read would force a
 *   meaningless Indeterminate case with no real trigger. AC-9 (the
 *   landing route's renewal-summary read, and the resume function's
 *   confirmed-sections read) consumes this type.
 *
 * Both are discriminated unions on `status`, so a `switch` over every
 * member combined with {@link assertUnreachable} in the `default` case is
 * a compile-time guarantee that no code path can produce an ambiguous or
 * missing outcome value — a branch TypeScript cannot prove exhaustive
 * fails to compile.
 */

/** Distinguishes "definitely did not happen" from "unknown, check before resubmitting" (ADR-20260821-kp45). Carried on every non-Succeeded outcome. */
export interface OutcomeFailureDetail {
  /**
   * The HTTP status the API surface returned. Vendored from
   * src/api/contracts/taxonomy-to-http.json's disjoint Failed/Indeterminate
   * status sets (feature 5 owns that file); never 502 (ADR-20260821-kp45).
   * This type does not import that file — it is a value the caller reads
   * off the response and carries here, not a compile-time constraint this
   * module could enforce, since the two tiers are not the same TypeScript
   * program.
   */
  httpStatus: number;
  /** The closed taxonomy code, where the API surface returned one (ADR-20260825-vta1). Not extended or reinterpreted by this feature. */
  taxonomyCode?: string;
  /**
   * Content-Design-authored, customer-facing wording where this failure
   * reaches a screen (ADR-20260826-94qx: Failed and Indeterminate carry
   * DISTINCT wording, approximately "that did not save" versus "we could
   * not confirm that saved"). This module carries whatever string the
   * caller supplies; it does not author copy itself.
   */
  message: string;
}

/** A write's outcome: Succeeded, Failed or Indeterminate. Never a fourth state, never omitted. */
export type WriteOutcome<TSuccess> =
  | { status: "Succeeded"; value: TSuccess }
  | { status: "Failed"; detail: OutcomeFailureDetail }
  | { status: "Indeterminate"; detail: OutcomeFailureDetail };

/** A read's outcome: Succeeded or Failed. No Indeterminate case exists for a read (ADR-20260918-gvy0). */
export type ReadOutcome<TSuccess> =
  | { status: "Succeeded"; value: TSuccess }
  | { status: "Failed"; detail: OutcomeFailureDetail };

/**
 * m1-portal-ux-fixes (Fix D, DEBT-20261023-sev7/DEBT-20261023-a7zv): the
 * closed set of write-refusal codes that mean "this renewal has already
 * been submitted", across every write surface this feature touches — the
 * working-copy PUT's own 409 `not_writable` (`src/api/functions/
 * working_copy.py`'s `_not_writable_response`) and the assignment/
 * reassign/take-back/contributor-section routes' shared 409
 * `renewal_submitted` (`src/api/submission_status.py`'s
 * `RENEWAL_SUBMITTED_CODE`, gated by `evaluate_submission_lock`). Named
 * once, here, so every write path recognises the same two strings rather
 * than a drifting per-screen copy of this list — a caller never shows an
 * error panel for either code; it routes the session straight to the
 * read-only `/renewal-status` view instead (see each write path's own
 * doc comment for where this is checked).
 */
export function isRenewalSubmittedWriteRefusal(code: string | undefined): boolean {
  return code === "not_writable" || code === "renewal_submitted";
}

/**
 * Exhaustiveness guard. A `switch (outcome.status)` covering every member of
 * {@link WriteOutcome} or {@link ReadOutcome} can pass `outcome` to this
 * function in its `default` branch; TypeScript only accepts the call if
 * every other case has already narrowed `outcome` to `never`, so a new
 * outcome member added later without updating every consuming switch fails
 * the build rather than falling through silently.
 */
export function assertUnreachable(outcome: never): never {
  throw new Error(
    `Unreachable outcome branch reached: ${JSON.stringify(outcome)}. Every WriteOutcome/ReadOutcome member must be handled explicitly — see src/web/src/state/outcome.ts.`,
  );
}
